import { ReactNode, useMemo, useState } from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ErrorEvidenceDto, ErrorOwner } from '../../api/api';
import { Dossier, DossierAction, DossierMoment, DossierNode, DossierSettlement, ROLE_LABEL } from '../../api/dossier';
import { Issue, IssueInvestigation, IssueRef, MODE_LABEL, SweeperStatus, sweepNow } from '../../api/issues';
import { describeError } from '../../api/requeue';
import { useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import { useIssueDossier } from '../../hooks/useIssueDossier';
import { BalanceBars } from '../Money/BalanceBars';
import { IdText } from '../Money/IdText';
import { FlowFailure, MoneyFlow } from '../Money/MoneyFlow';
import { ObjectFlow } from '../Money/ObjectFlow';
import { clock, missingInExact, stamp } from '../Money/money';
import { date, dossierLink } from './issueFormat';
import './IssueDetail.scss';

export interface IIssueDetailProps {
    issue: Issue;
    busy?: boolean;
    copied?: boolean;
    onCopy?: () => void;
    onClose?: (items: IssueRef[], what: string) => void;
    onReopen?: (items: IssueRef[]) => void;
    /** Opens "Relatie kiezen" for the customer of the issue. */
    onRelink?: (issue: Issue) => void;
    /** Opens "Koppelen": the document next to the booking Exact has of it already. */
    onLink?: (issue: Issue) => void;
    /** Goes to the mail to the customer of the issue's environment. */
    onMail?: (issue: Issue) => void;
    /** Puts the kind of problem of the issue on "in onderzoek" and copies it for the skill. */
    onInvestigate?: () => void;
    /** Who looks into the kind of problem of the issue already, if anyone. */
    investigation?: IssueInvestigation | null;
    /** How the scanner stands: whether anything is offered again by itself, and for real. */
    scanner?: SweeperStatus | null;
}

type Lane = 'us' | 'customer' | 'automatic' | 'done';

const LANE: Record<Lane, { label: string; headline: string; icon: IconProp }> = {
    us: { label: 'Wij', headline: 'Wij moeten iets doen', icon: 'gear' },
    customer: { label: 'Klant', headline: 'Wacht op de klant', icon: 'user' },
    automatic: { label: 'Automatisch', headline: 'Gaat vanzelf', icon: 'rotate' },
    done: { label: 'Klaar', headline: 'Opgelost', icon: 'circle-check' },
};

const isOpen = (issue: Issue) => issue.status !== 'Resolved' && issue.status !== 'Closed';

const refOf = (issue: Issue): IssueRef => ({ customer: issue.customer, environment: issue.environment, key: issue.key });

/** Whose move it is, from where the issue stands now rather than from the rule. */
const laneOf = (issue: Issue): Lane => {
    switch (issue.status) {
        case 'WaitingOnCustomer':
            return 'customer';
        case 'WaitingOnUs':
            return 'us';
        case 'NeedsRequeue':
        case 'ResolvesItself':
            return 'automatic';
        case 'Resolved':
        case 'Closed':
            return 'done';
        default:
            return issue.interpretation?.owner === ErrorOwner.Customer
                ? 'customer'
                : issue.interpretation?.owner === ErrorOwner.Automatic
                  ? 'automatic'
                  : 'us';
    }
};

/**
 * Why an issue that should go by itself does not, as things are set up: the scanner may not offer anything
 * again, or this API only pretends to. Null when nothing stands in the way.
 */
const notByItself = (issue: Issue, scanner?: SweeperStatus | null): string | null => {
    if (!scanner || issue.status !== 'NeedsRequeue') {
        return null;
    }

    if (scanner.autoRequeue === 'Off') {
        return 'opnieuw aanbieden staat uit';
    }

    if (scanner.autoRequeue === 'Propose' && issue.autoRequeueState !== 'Approved') {
        return 'de scanner stelt alleen voor; iemand moet het goedkeuren bij Voorstellen';
    }

    if (!scanner.allowRequeue) {
        return 'dit dashboard proefdraait alleen (IssueSweep:AllowRequeue staat uit), er gaat niets echt naar de wachtrij';
    }

    if (issue.firstFailedAt && Date.now() - new Date(issue.firstFailedAt).getTime() > scanner.autoRequeueMaxAgeDays * 86_400_000) {
        return `ouder dan ${scanner.autoRequeueMaxAgeDays} dagen: de scanner biedt het niet meer vanzelf aan`;
    }

    return null;
};

const nodeLabel = (node?: DossierNode) => (node ? `${ROLE_LABEL[node.role] ?? node.role} ${node.relationId}` : '');

/** The failures of the connector around the invoice, for the middle lane of the flow. */
const failuresOf = (dossier: Dossier, settlement: DossierSettlement, issue: Issue): FlowFailure[] => {
    const nodes = new Map(dossier.nodes.map((node) => [node.key, node]));
    const belongs = (moment: DossierMoment) => {
        const node = nodes.get(moment.nodeKey);

        return moment.nodeKey === issue.key || moment.nodeKey === settlement.invoiceKey || node?.parentKey === settlement.invoiceKey;
    };

    return dossier.moments
        .filter((moment) => moment.kind === 'Failed' && belongs(moment))
        .map((moment) => ({
            key: moment.id,
            at: moment.firstAt,
            lastAt: moment.lastAt,
            count: moment.count,
            title: moment.interpretation?.title ?? moment.headline,
            subject: nodeLabel(nodes.get(moment.nodeKey)),
            focus: moment.nodeKey === issue.key,
        }));
};

/** The gap that says Exact was not read this time, if there is one. */
const exactGap = (dossier: Dossier | null) => dossier?.gaps.find((gap) => gap.startsWith('Exact is niet geraadpleegd') || gap.startsWith('Exact gaf'));

/** Everything a person or the skill needs to look into this issue, in one piece. */
const forResearch = (issue: Issue, dossier: Dossier | null, scanner?: SweeperStatus | null) => ({
    request: 'Zoek uit wat er met dit issue aan de hand is en of de uitleg en afhandeling kloppen met wat Chargebee, Exact en de logboeken laten zien.',
    format: 'issue-research/1',
    exportedAt: new Date().toISOString(),
    issue,
    scanner: scanner
        ? { autoRequeue: scanner.autoRequeue, autoRequeueChosen: scanner.autoRequeueChosen, allowRequeue: scanner.allowRequeue, enabled: scanner.enabled, maxAgeDays: scanner.autoRequeueMaxAgeDays }
        : null,
    dossier: dossier ? { ...dossier, moments: dossier.moments.map(({ occurrences, ...moment }) => moment) } : null,
});

/**
 * One issue opened up, as an answer rather than a text: whose move it is and the button for it, what
 * the error itself shows, how the money went in Chargebee and Exact - read live when the row opens -,
 * who does what from here, and the technical rest folded away. Where the live reading says the issue is
 * past, that goes before what the scanner concluded earlier.
 */
export const IssueDetail = ({ issue, busy, copied, onCopy, onClose, onReopen, onRelink, onLink, onMail, onInvestigate, investigation, scanner }: IIssueDetailProps) => {
    const axios = useAxiosSwagger();
    const rule = issue.interpretation;
    const open = isOpen(issue);
    const checked = !!issue.checksAt;
    const evidence = (rule?.evidence ?? []).filter((e) => e.label && e.value);
    const canInvestigate = !rule && open && !investigation && !!onInvestigate;
    const canRelink = !!onRelink && open && (issue.canChooseAccount || issue.autoAction === 'Relink');
    const canLink = !!onLink && open && issue.autoAction === 'Link';
    const canMail = !!onMail && issue.status === 'WaitingOnCustomer';

    const { dossier, loading, error, readAt, reload } = useIssueDossier(issue.customer, issue.environment, issue.invoiceId ?? issue.relationId);
    // The invoice of the issue, or the one the dossier found for it - a chargeback leads there through its payment.
    const settlement = useMemo(() => {
        const invoiceId = issue.invoiceId ?? (dossier?.kind === 'Invoice' ? dossier.rootId : issue.relationId);

        return dossier?.settlements?.find((s) => s.invoiceId === invoiceId) ?? null;
    }, [dossier, issue.invoiceId, issue.relationId]);
    const failures = useMemo(() => (dossier && settlement ? failuresOf(dossier, settlement, issue) : []), [dossier, settlement, issue]);
    // Only what concerns this issue's own object or invoice: a customer's dossier has the actions of all its invoices.
    const ourActions = (dossier?.actions ?? []).filter(
        (a) =>
            a.owner === ErrorOwner.Support &&
            a.code !== issue.code &&
            (a.nodeKeys.includes(issue.key) || (!!settlement && a.nodeKeys.includes(settlement.invoiceKey)))
    );
    const missingExact = exactGap(dossier);

    // Read live, the invoice is settled in both systems: whatever the scanner concluded before is past.
    const settledLive =
        open &&
        !!settlement &&
        !missingInExact(settlement) &&
        settlement.openInChargebee === 0 &&
        settlement.openInExact === 0 &&
        (settlement.verdict === 'InStep' || settlement.verdict === 'WasOutOfStep');

    const stuck = open && !settledLive ? notByItself(issue, scanner) : null;
    const lane: Lane = settledLive ? 'done' : laneOf(issue);

    const [copiedAll, setCopiedAll] = useState<boolean>(false);
    const [acting, setActing] = useState<string | null>(null);
    const [outcome, setOutcome] = useState<{ ok: boolean; text: string } | null>(null);

    // What to do, in one line. A rule that read the facts out of the error says it for this case; otherwise the
    // scanner's own conclusion where it checks this itself; otherwise the rule's general line.
    const todo = settledLive
        ? `In Chargebee en Exact staat niets meer open (live gelezen om ${clock(settlement?.readAt)}). De scanner zet het issue bij de volgende ronde op opgelost.`
        : stuck
          ? `Gaat niet vanzelf: ${stuck}. Dat blijft zo tot vaststaat dat een nieuwe poging nooit dubbel of verkeerd boekt; tot dan blijft dit open staan.`
          : ((evidence.length > 0 && open ? rule?.action : null) ??
            (checked && open ? issue.resolution : null) ??
            rule?.action ??
            (rule ? null : 'Nog geen regel kent deze fout.'));
    const steps = checked || issue.checkable ? [] : rule?.steps ?? [];

    const copyAll = () => {
        navigator.clipboard
            ?.writeText(JSON.stringify(forResearch(issue, dossier, scanner), null, 2))
            .then(() => {
                setCopiedAll(true);
                window.setTimeout(() => setCopiedAll(false), 2000);
            })
            .catch(() => setCopiedAll(false));
    };

    const scan = () => {
        if (!axios) {
            return;
        }

        setActing('scan');
        setOutcome(null);

        sweepNow(axios, issue.customer, issue.environment)
            .then(() => setOutcome({ ok: true, text: 'De scanner heeft deze omgeving opnieuw bekeken; ververs de lijst om de nieuwe stand te zien.' }))
            .catch((ex) => setOutcome({ ok: false, text: describeError(ex) }))
            .finally(() => setActing(null));
    };

    const primary: ReactNode = settledLive ? (
        <button type="button" className="btn btn-sm btn-primary" disabled={busy || !!acting} onClick={scan} title="Laat de scanner deze omgeving nu bekijken, zodat het issue op opgelost komt">
            <FontAwesomeIcon icon={acting === 'scan' ? 'spinner' : 'rotate'} spin={acting === 'scan'} /> Scanner nu laten kijken
        </button>
    ) : canInvestigate ? (
        <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={onInvestigate} title="Zet dit probleem op 'in onderzoek' en kopieert het voor de skill c2e-research-error">
            <FontAwesomeIcon icon="magnifying-glass" /> {copied ? 'Gekopieerd, plak in Claude Code' : 'Onderzoek dit'}
        </button>
    ) : canLink ? (
        <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={() => onLink?.(issue)} title="Vergelijk het Chargebee-document met de boeking die Exact al heeft en koppel ze">
            <FontAwesomeIcon icon="link" /> Koppelen
        </button>
    ) : canRelink ? (
        <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={() => onRelink?.(issue)} title="Vergelijk de Chargebee-klant met de relaties in Exact en koppel om">
            <FontAwesomeIcon icon="link" /> Relatie kiezen
        </button>
    ) : canMail ? (
        <button type="button" className="btn btn-sm btn-primary" onClick={() => onMail?.(issue)}>
            <FontAwesomeIcon icon="envelope" /> Klantmail opstellen
        </button>
    ) : null;

    return (
        <div className="story">
            <header className={`story__verdict story__verdict--${lane}`}>
                <span className="story__who">
                    <FontAwesomeIcon icon={LANE[lane].icon} />{' '}
                    {issue.status === 'Closed' ? 'Gesloten' : settledLive ? 'Lijkt opgelost' : stuck ? 'Gaat niet vanzelf' : LANE[lane].headline}
                </span>
                <div className="story__headline">
                    <strong className="story__title">{rule?.title ?? issue.title}</strong>
                    {todo && open && (
                        <span className="story__todo">
                            <IdText text={todo} />
                        </span>
                    )}
                    {issue.status === 'Closed' && (
                        <span className="story__todo">
                            Gesloten op {date(issue.closedAt)}
                            {issue.closedBy ? ` door ${issue.closedBy}` : ''}.{issue.closedReason ? ` Reden: ${issue.closedReason}.` : ''} Faalt het object opnieuw, dan gaat het weer open.
                        </span>
                    )}
                    {issue.status === 'Resolved' && issue.resolution && issue.resolution.replace(/\.$/, '') !== (rule?.title ?? issue.title) && (
                        <span className="story__todo">{issue.resolution}</span>
                    )}
                    {issue.reviewNote && (
                        <span className="story__todo issues__muted">
                            Herbeoordeling op {date(issue.reviewedAt)}
                            {issue.reviewedBy ? ` door ${issue.reviewedBy}` : ''}: {issue.reviewNote}
                        </span>
                    )}
                </div>
                <div className="story__actions">
                    {primary}
                    <a className="btn btn-sm btn-outline-secondary" href={dossierLink(issue.customer, issue.environment, issue.invoiceId ?? issue.relationId)} target="_blank" rel="noopener noreferrer">
                        <FontAwesomeIcon icon="arrow-up-right-from-square" /> Dossier
                    </a>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={copyAll}
                        disabled={loading}
                        title="Het issue, wat Chargebee en Exact nu zeggen en het hele dossier als JSON, om te plakken in Claude Code"
                    >
                        <FontAwesomeIcon icon="copy" /> {copiedAll ? 'Gekopieerd' : 'Kopieer'}
                    </button>
                    {onClose && open && (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            disabled={busy}
                            onClick={() => onClose([refOf(issue)], `Issue ${issue.relationId} sluiten`)}
                            title="Voor een issue dat er niet meer toe doet. Het gaat weer open als het object opnieuw faalt."
                        >
                            <FontAwesomeIcon icon="box-archive" /> Sluiten
                        </button>
                    )}
                    {onReopen && issue.status === 'Closed' && (
                        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={busy} onClick={() => onReopen([refOf(issue)])}>
                            <FontAwesomeIcon icon="rotate-left" /> Weer openen
                        </button>
                    )}
                </div>
            </header>

            {outcome && (
                <p className={`story__notice${outcome.ok ? ' story__notice--ok' : ' story__notice--error'}`}>
                    <FontAwesomeIcon icon={outcome.ok ? 'circle-check' : 'circle-xmark'} /> {outcome.text}
                </p>
            )}

            {(evidence.length > 0 || rule?.explanation || !rule) && (
                <section className="story__section">
                    <h5 className="story__label">Wat er misging{settledLive ? ' (toen)' : ''}</h5>
                    {evidence.length > 0 ? (
                        <Evidence items={evidence} />
                    ) : rule?.explanation ? (
                        <p className="story__text">{rule.explanation}</p>
                    ) : (
                        issue.errorMessage && <blockquote className="story__quote">{issue.errorMessage.length > 320 ? `${issue.errorMessage.slice(0, 320)}…` : issue.errorMessage}</blockquote>
                    )}
                    {evidence.length > 0 && rule?.explanation && <p className="story__text story__text--muted">{rule.explanation}</p>}
                </section>
            )}

            <section className="story__section">
                <div className="story__section-head">
                    <h5 className="story__label">{settlement ? `Geldstroom van factuur ${settlement.invoiceId}` : 'Verloop'}</h5>
                    <span className="story__fresh">
                        {loading ? (
                            <>
                                <FontAwesomeIcon icon="spinner" spin /> Chargebee en Exact worden gelezen…
                            </>
                        ) : readAt ? (
                            <>
                                Live gelezen om {clock(new Date(readAt).toISOString())}
                                {missingExact ? ', Exact niet' : ''}{' '}
                                <button type="button" className="btn btn-sm btn-link story__reload" onClick={reload}>
                                    <FontAwesomeIcon icon="rotate" /> opnieuw
                                </button>
                            </>
                        ) : null}
                    </span>
                </div>

                {error && (
                    <p className="story__notice story__notice--error">
                        <FontAwesomeIcon icon="circle-xmark" /> Het dossier kon niet gelezen worden: {error}{' '}
                        <button type="button" className="btn btn-sm btn-link" onClick={reload}>
                            Opnieuw proberen
                        </button>
                    </p>
                )}

                {missingExact && !loading && (
                    <p className="story__notice">
                        <FontAwesomeIcon icon="circle-info" /> {missingExact}{' '}
                        <button type="button" className="btn btn-sm btn-link" onClick={reload}>
                            Exact opnieuw lezen
                        </button>
                    </p>
                )}

                {loading && !dossier && <div className="story__skeleton" aria-hidden="true" />}

                {settlement && (
                    <div className="story__money">
                        <BalanceBars settlement={settlement} />
                        <MoneyFlow settlement={settlement} failures={failures} highlight={issue.relationId} />
                    </div>
                )}

                {dossier && !settlement && <ObjectFlow dossier={dossier} focusKey={issue.key} />}
            </section>

            <section className="story__section">
                <h5 className="story__label">Wie doet wat</h5>
                <div className="story__lanes">
                    <LaneCard lane="automatic" active={lane === 'automatic'}>
                        <AutomaticText issue={issue} scanner={scanner} stuck={stuck} settledLive={settledLive} />
                    </LaneCard>
                    <LaneCard lane="us" active={lane === 'us' || ourActions.length > 0}>
                        {lane === 'us' && open ? <Todo text={todo} steps={steps} /> : ourActions.length === 0 && <span className="story__none">Niets te doen.</span>}
                        {ourActions.map((action) => (
                            <ActionNote key={action.code} action={action} />
                        ))}
                        {lane === 'us' && open && (canLink || canRelink || canInvestigate) && <div className="story__lane-buttons">{primary}</div>}
                    </LaneCard>
                    <LaneCard lane="customer" active={lane === 'customer'}>
                        {lane === 'customer' && open ? <Todo text={todo} steps={steps} /> : <span className="story__none">Niets te doen.</span>}
                        {issue.customerNotifiedAt && (
                            <span className="story__meta">
                                <FontAwesomeIcon icon="envelope" /> Gemeld op {date(issue.customerNotifiedAt)}
                            </span>
                        )}
                        {canMail && !settledLive && (
                            <div className="story__lane-buttons">
                                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => onMail?.(issue)}>
                                    <FontAwesomeIcon icon="envelope" /> Klantmail
                                </button>
                            </div>
                        )}
                    </LaneCard>
                </div>
            </section>

            {checked && (issue.checks ?? []).length > 0 && (
                <section className="story__section">
                    <h5 className="story__label">
                        De scanner controleerde <span className="story__when">op {stamp(issue.checksAt)}{issue.checkedInExact ? ', ook in Exact' : ''}</span>
                    </h5>
                    <ul className="story__checks">
                        {issue.checks.map((check) => (
                            <li key={check.text} className={check.ok ? 'is-ok' : 'is-missing'}>
                                <FontAwesomeIcon icon={check.ok ? 'circle-check' : 'circle-xmark'} /> <IdText text={check.text} />
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            <details className="story__tech">
                <summary>Technische details</summary>
                <dl className="story__facts">
                    <div>
                        <dt>Object</dt>
                        <dd>
                            {issue.objectType} <IdText text={issue.relationId} />
                        </dd>
                    </div>
                    {issue.invoiceId && (
                        <div>
                            <dt>Factuur</dt>
                            <dd>
                                <IdText text={issue.invoiceId} />
                            </dd>
                        </div>
                    )}
                    {issue.chargebeeCustomerId && (
                        <div>
                            <dt>Chargebee-klant</dt>
                            <dd>{issue.chargebeeCustomerId}</dd>
                        </div>
                    )}
                    {issue.attempts === 0 && issue.code === 'never-arrived' ? (
                        <div>
                            <dt>In Chargebee sinds</dt>
                            <dd>{stamp(issue.firstFailedAt)}, nooit bij de connector aangekomen</dd>
                        </div>
                    ) : (
                        <div>
                            <dt>Pogingen</dt>
                            <dd>
                                {issue.attempts}, van {stamp(issue.firstFailedAt)} tot {stamp(issue.lastFailedAt)}
                            </dd>
                        </div>
                    )}
                    {issue.location && (
                        <div>
                            <dt>Plek in de code</dt>
                            <dd>
                                <code>{issue.location}</code>
                            </dd>
                        </div>
                    )}
                    {rule?.code && (
                        <div>
                            <dt>Regel</dt>
                            <dd>
                                <code>{rule.code}</code>
                            </dd>
                        </div>
                    )}
                </dl>
                {issue.errorMessage && (
                    <pre className="story__raw">
                        {issue.errorType ? `${issue.errorType}\n` : ''}
                        {issue.errorMessage}
                    </pre>
                )}
                {onCopy && !canInvestigate && (
                    <button type="button" className="btn btn-sm btn-link" onClick={onCopy} title="Dit soort probleem met voorbeelden als JSON voor de skill c2e-research-error">
                        <FontAwesomeIcon icon="copy" /> {copied ? 'Gekopieerd' : 'Kopieer dit soort probleem voor de skill'}
                    </button>
                )}
            </details>
        </div>
    );
};

const Evidence = ({ items }: { items: ErrorEvidenceDto[] }) => (
    <dl className="story__evidence">
        {items.map((item) => (
            <div
                key={`${item.label}|${item.value}`}
                className={`story__fact${item.warning ? ' story__fact--warn' : ''}${(item.value?.length ?? 0) > 32 ? ' story__fact--text' : ''}`}
            >
                <dt>
                    {item.warning && <FontAwesomeIcon icon="triangle-exclamation" />} <IdText text={item.label} />
                </dt>
                <dd>
                    <span className="story__fact-value">{item.value}</span>
                    {item.note && <span className="story__fact-note">{item.note}</span>}
                </dd>
            </div>
        ))}
    </dl>
);

const LaneCard = ({ lane, active, children }: { lane: Lane; active: boolean; children: ReactNode }) => (
    <div className={`story__lane story__lane--${lane}${active ? ' is-active' : ''}`}>
        <span className="story__lane-head">
            <FontAwesomeIcon icon={LANE[lane].icon} /> {LANE[lane].label}
        </span>
        {children}
    </div>
);

const Todo = ({ text, steps }: { text?: string | null; steps: string[] }) => (
    <>
        {text && (
            <span className="story__lane-todo">
                <IdText text={text} />
            </span>
        )}
        {steps.length > 0 && (
            <ol className="story__steps">
                {steps.map((step) => (
                    <li key={step}>
                        <IdText text={step} />
                    </li>
                ))}
            </ol>
        )}
    </>
);

/** What the scanner does about it by itself, as it is set up now, so nobody waits for something that will not come. */
const AutomaticText = ({ issue, scanner, stuck, settledLive }: { issue: Issue; scanner?: SweeperStatus | null; stuck: string | null; settledLive: boolean }) => {
    const open = isOpen(issue);
    const lines: string[] = [];

    if (!open) {
        lines.push(issue.status === 'Resolved' ? 'Niets meer: het object is waar het hoort.' : 'Niets: het issue is gesloten.');
    } else {
        if (scanner && !scanner.enabled) {
            lines.push("De scantimer staat uit: er wordt alleen gekeken bij 'Nu scannen'.");
        }

        if (settledLive) {
            lines.push('Ziet bij de volgende scanronde dat het opgelost is en zet het issue dan op opgelost.');
        } else if (issue.checkable) {
            lines.push(
                issue.checksAt
                    ? `Controleert dit elke scanronde zelf, laatst op ${stamp(issue.checksAt)}. Is het opgelost, dan sluit het issue vanzelf.`
                    : 'Controleert dit bij de volgende scanronde zelf.'
            );
        }

        if (issue.autoActionText) {
            lines.push(`Voorstel: ${issue.autoActionText}${issue.autoRequeueState === 'Proposed' ? ' (wacht op goedkeuring).' : ''}`);
        }

        if (stuck) {
            lines.push(`Biedt het niet opnieuw aan: ${stuck}. Zodra een nieuwe poging aantoonbaar nooit dubbel boekt, kan dit aan.`);
        } else if (!settledLive && (issue.status === 'NeedsRequeue' || issue.status === 'ResolvesItself')) {
            lines.push(
                issue.nextAutoRequeueAt
                    ? `Biedt het opnieuw aan om ${stamp(issue.nextAutoRequeueAt)}.`
                    : issue.autoRequeueCount > 0
                      ? `Al ${issue.autoRequeueCount} keer opnieuw aangeboden.`
                      : issue.status === 'ResolvesItself'
                        ? 'De connector probeert het zelf nog een paar keer.'
                        : `Biedt het opnieuw aan bij een volgende ronde${scanner ? ` (modus: ${MODE_LABEL[scanner.autoRequeue] ?? scanner.autoRequeue})` : ''}.`
            );
        }

        if (issue.autoRequeueNote) {
            lines.push(`Laatste melding: ${issue.autoRequeueNote}`);
        }
    }

    return lines.length > 0 ? (
        <ul className="story__auto">
            {lines.map((line) => (
                <li key={line}>{line}</li>
            ))}
        </ul>
    ) : (
        <span className="story__none">Doet hier zelf niets mee.</span>
    );
};

const ActionNote = ({ action }: { action: DossierAction }) => (
    <span className="story__extra">
        <FontAwesomeIcon icon="triangle-exclamation" /> <strong>{action.title}.</strong> <IdText text={action.action} />
    </span>
);
